import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Alert,
} from '@predoc/cow';
import { Hospital, Info } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { getProfileColor } from '@/utils';
import { ExistingPatient } from '../../../types';

export const ExistingPatientConfirm = ({
  open,
  setExistingModalOpen,
  patient,
  handleBackToPatientDetails,
  handleUseExistingPatient,
}: {
  open: boolean;
  setExistingModalOpen: (open: boolean) => void;
  patient: ExistingPatient | null;
  handleBackToPatientDetails?: () => void;
  handleUseExistingPatient?: () => void;
}) => {
  const color = getProfileColor(
    patient?.organizationProfile.avatarType ?? 'DEFAULT_GRAY',
  );
  return (
    <Dialog open={open} onOpenChange={setExistingModalOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader className="border-b pb-6">
          <DialogTitle>Existing Patient Found</DialogTitle>
          <DialogDescription className="sr-only">
            Review the matching patient before continuing.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md">
          <Alert variant="accent">
            <Info size={16} />
            We found an existing patient with the same Name, Date of Birth, and
            ZIP Code.
          </Alert>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
          <div>
            <div className="text-muted-foreground text-sm">Name</div>
            <div className="mt-1 font-medium">
              {patient ? `${patient.firstName} ${patient.lastName}` : '-'}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground text-sm">
              Patient Reference ID
            </div>
            <div className="mt-1 font-medium break-all">
              {patient?.patientReferenceId || '-'}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground text-sm">Date of Birth</div>
            <div className="mt-1 font-medium">
              {patient
                ? format(parseISO(patient.dateOfBirth), 'MM/dd/yyyy')
                : '-'}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground text-sm">Postal/ZIP Code</div>
            <div className="mt-1 font-medium">
              {patient?.address.postalCode ?? '-'}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground text-sm">Profile</div>
            <div className="mt-1 flex items-center gap-2 font-medium">
              <Hospital size={16} style={{ color }} aria-hidden="true" />
              <span>{patient?.organizationProfile.displayName ?? '-'}</span>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-6 flex w-full items-center justify-end gap-3 border-t pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={handleBackToPatientDetails}
            aria-label="Back to Patient Details"
          >
            Back to Patient Details
          </Button>
          <Button
            type="button"
            onClick={handleUseExistingPatient}
            aria-label="Use Existing Patient"
          >
            Use Existing Patient
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
